import React, { useState, useEffect, useCallback, useRef, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { X, Edit2, TrendingUp, ChevronLeft, MessageSquare } from 'lucide-react';
import { db } from './firebase';
import './App.css';
import LoginPage from './components/auth/LoginPage';
import MobileHeader from './components/navigation/MobileHeader';
import MobileMenu from './components/navigation/MobileMenu';
import DesktopSidebar from './components/navigation/DesktopSidebar';
import WalletConnect from './components/wallet/WalletConnect';
import { walletService } from './utils/wallet';
import logger from './utils/logger';
import crowdSourcedChecker from './services/crowdSourcedChecker';
import PerformanceMonitor from './components/performance/PerformanceMonitor';
import LoadingSpinner from './components/common/LoadingSpinner';
import EmailVerificationBanner from './components/common/EmailVerificationBanner';
import ActiveBoostIndicator from './components/boosts/ActiveBoostIndicator';
import xpService from './services/xpService';
import userService from './services/userService';
import { formatTimestamp } from './utils/helpers';
import { useAuth } from './hooks/useAuth';
import { useMessaging } from './hooks/useMessaging';
import { usePosts } from './hooks/usePosts';
import AppContext from './contexts/AppContext';
import AuthContext from './contexts/AuthContext';
import PostsContext from './contexts/PostsContext';
import {
  collection,
  addDoc,
  query,
  orderBy,
  limit,
  onSnapshot,
  serverTimestamp,
  doc,
  getDoc,
  updateDoc,
  arrayUnion,
  arrayRemove,
  where,
  getDocs
} from 'firebase/firestore';

// Lazy load heavy components
const LiveChat = lazy(() => import('./components/chat/LiveChat'));
const ProfileView = lazy(() => import('./components/profile/ProfileView'));
const PostDetailView = lazy(() => import('./components/posts/PostDetailView'));
const CommentModal = lazy(() => import('./components/modals/CommentModal'));
const FollowModal = lazy(() => import('./components/modals/FollowModal'));
const RetweetModal = lazy(() => import('./components/modals/RetweetModal'));
const LikesModal = lazy(() => import('./components/modals/LikesModal'));
const TermsTab = lazy(() => import('./components/tabs/TermsTab'));
const TokenomicsTab = lazy(() => import('./components/tabs/TokenomicsTab'));
const MessagesTab = lazy(() => import('./components/tabs/MessagesTab'));
const CryptoWidget = lazy(() => import('./components/crypto/CryptoWidget'));
const Leaderboard = lazy(() => import('./components/stats/Leaderboard'));
const PlatformStats = lazy(() => import('./components/stats/PlatformStats'));
const NotificationsList = lazy(() => import('./components/notifications/NotificationsList'));
const PopupNotification = lazy(() => import('./components/notifications/PopupNotification'));
const HomeTab = lazy(() => import('./components/tabs/HomeTab'));
const ExploreTab = lazy(() => import('./components/tabs/ExploreTab'));
const BetterMoversTab = lazy(() => import('./components/tabs/BetterMoversTab'));
const FollowingTab = lazy(() => import('./components/tabs/FollowingTab'));
const RankUpTab = lazy(() => import('./components/tabs/LevelUpTab'));
const Analytics = lazy(() => import('./components/admin/XPDashboard'));





// Main TopBlast Component (now with routing)
const TopBlastApp = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // ===== Custom Hooks =====
  const authState = useAuth();
  const {
    isLoggedIn, authLoading, browsingMode, currentUser, setCurrentUser,
    error, setError, showSignup, setShowSignup, loginForm, setLoginForm,
    signupForm, setSignupForm, authMode, setAuthMode, emailForPasswordless,
    setEmailForPasswordless, resetEmailSent, emailLinkSent, emailVerificationSent,
    hasPendingRankUp, setHasPendingRankUp, following, setFollowing,
    blockedUsers, setBlockedUsers, handleSignup, handleLogin, handlePasswordlessLogin,
    handlePasswordReset, handleSignupWithoutVerification, resendEmailVerification,
    handleLogout: authLogout, handleBrowseWithoutAccount,
  } = authState;

  // Toast state (needed by usePosts)
  const [toastMessage, setToastMessage] = useState(null);

  const postsState = usePosts({
    currentUser, setCurrentUser, isLoggedIn, browsingMode, setToastMessage,
  });
  const {
    posts, setPosts, loading, newPost, setNewPost, postType, setPostType,
    showTradingPost, setShowTradingPost, feedFilter, setFeedFilter,
    setPostImage, postImagePreview, setPostImagePreview,
    uploadingImage, postSentiment, setPostSentiment, hiddenPosts, setHiddenPosts,
    activeDropdown, setActiveDropdown, popularTimeFilter, setPopularTimeFilter,
    showCommentModal, setShowCommentModal, selectedPost, setSelectedPost,
    newComment, setNewComment, viewingPost, setViewingPost, postComments,
    setPostComments, replyingTo, setReplyingTo, handleImageSelect,
    handlePost, handleTradingPost, likePost, retweetPost, handleComment,
    likeComment, handleDeleteComment, handleDeletePost, handlePinPost,
    getPopularPosts,
  } = postsState;

  const messagingState = useMessaging({ currentUser, blockedUsers });
  const {
    conversations, selectedConversation, setSelectedConversation,
    messages, newDM, setNewDM, dmRecipient, setDmRecipient,
    unreadDMs, dmImage, setDmImage, dmImagePreview, setDmImagePreview,
    sendingDM, suggestedDMUsers, setSuggestedDMUsers, userSearchResults,
    setUserSearchResults, userSearchQuery, setUserSearchQuery,
    sendDM, markMessagesAsRead, loadConversations, loadMessages,
    createNewConversation, searchUsers, handleDMImageSelect,
  } = messagingState;

  // ===== Remaining local state =====
  const mainContentRef = useRef(null);
  const [showFloatingButton, setShowFloatingButton] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [liveChat, setLiveChat] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [activeTab, setActiveTab] = useState(() => {
    return localStorage.getItem('activeTab') || 'home';
  });
  const [selectedUser, setSelectedUser] = useState(null);
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({ username: '', bio: '', displayPreference: 'marketcap' });
  const [userStats, setUserStats] = useState({ followers: 0, following: 0 });
  const [darkMode, setDarkMode] = useState(true);
  const [showFollowModal, setShowFollowModal] = useState(false);
  const [followModalType, setFollowModalType] = useState('followers');
  const [followModalUsers, setFollowModalUsers] = useState([]);
  const [loadingFollowModal, setLoadingFollowModal] = useState(false);
  const [showRetweetModal, setShowRetweetModal] = useState(false);
  const [retweetModalPostId, setRetweetModalPostId] = useState(null);
  const [retweetModalUsers, setRetweetModalUsers] = useState([]);
  const [loadingRetweetModal, setLoadingRetweetModal] = useState(false);
  const [showLikesModal, setShowLikesModal] = useState(false);
  const [likesModalPostId, setLikesModalPostId] = useState(null);
  const [likesModalUsers, setLikesModalUsers] = useState([]);
  const [loadingLikesModal, setLoadingLikesModal] = useState(false);
  const [suggestedUser, setSuggestedUser] = useState(null);
  const [suggestedUserStats, setSuggestedUserStats] = useState({ followers: 0 });
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [userWallet, setUserWallet] = useState(null);
  const [showWalletModal, setShowWalletModal] = useState(false);
  const [walletKey, setWalletKey] = useState(0);
  const [popupNotifications, setPopupNotifications] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [viewingImage, setViewingImage] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Wrap authLogout to also clear posts/chat
  const handleLogout = async () => {
    await authLogout();
    setPosts([]);
    setLiveChat([]);
  };

  // Minimize state for sidebars only
  const [minimizedSections, setMinimizedSections] = useState({
    chat: false,
    stats: false
  });

  // Auto-minimize sidebars on smaller screens or when wallet trades is active
  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      // More aggressive auto-minimize for better UX
      const shouldAutoMinimize = width < 1920 || showTradingPost; // Auto-minimize below 1920px or when trading modal is open
      
      logger.log(`🖥️ Screen width: ${width}px, Trading modal: ${showTradingPost}, Should minimize: ${shouldAutoMinimize}`);
      
      if (shouldAutoMinimize) {
        logger.log('🔽 Auto-minimizing sidebars for better space');
        setMinimizedSections({
          chat: true,
          stats: true
        });
      } else if (!shouldAutoMinimize && width >= 1920 && !showTradingPost) {
        logger.log('🔼 Auto-expanding sidebars on large screen');
        setMinimizedSections({
          chat: false,
          stats: false
        });
      }
    };

    // Initial check
    checkScreenSize();
    
    // Check on resize
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, [showTradingPost]); // Removed minimizedSections from deps to prevent infinite loop
  
  
  // Auto-hide toast message
  useEffect(() => {
    if (toastMessage) {
      const timer = setTimeout(() => {
        setToastMessage(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [toastMessage]);
  
  // Toggle minimize for sidebar sections
  const toggleMinimizeSection = (section) => {
    setMinimizedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  
  // Helper function to handle tab changes with scroll reset
  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    localStorage.setItem('activeTab', newTab);
    
    // Save main page (not chat/leaderboard) for tab navigation
    if (newTab !== 'chat' && newTab !== 'leaderboard') {
      localStorage.setItem('lastMainPage', newTab);
    }
    
    // Navigate to the appropriate route
    const routeMap = {
      'home': '/',
      'big-movers': '/big-movers',
      'following': '/following',
      'explore': '/explore',
      'profile': '/profile',
      'rank-up': '/rank-up',
      'level-up': '/rank-up',  // Map level-up to rank-up route
      'notifications': '/notifications',
      'xp-dashboard': '/analytics',
      'messages': '/messages',
      'chat': '/chat',
      'leaderboard': '/stats',
      'terms': '/terms',
      'tokenomics': '/tokenomics',
      'post': '/post'
    };
    
    const route = routeMap[newTab] || '/';
    navigate(route);
    
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  // Sync URL with active tab on component mount and route changes
  useEffect(() => {
    const pathToTab = {
      '/': 'home',
      '/big-movers': 'big-movers',
      '/following': 'following',
      '/explore': 'explore',
      '/profile': 'profile',
      '/rank-up': 'level-up',  // Map to level-up since that's what the app uses
      '/notifications': 'notifications',
      '/analytics': 'xp-dashboard',
      '/messages': 'messages',
      '/chat': 'chat',
      '/stats': 'leaderboard',
      '/terms': 'terms',
      '/tokenomics': 'tokenomics',
      '/post': 'post'
    };
    
    const currentTab = pathToTab[location.pathname];
    if (currentTab && currentTab !== activeTab) {
      setActiveTab(currentTab);
    }
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle viewing a post with smooth scroll to content area
  const handleViewPost = (post) => {
    setViewingPost(post);
    setActiveTab('post');
    // Scroll to just below the header
    setTimeout(() => {
      if (mainContentRef.current) {
        const headerHeight = 64; // Height of mobile/desktop header
        const scrollPosition = mainContentRef.current.offsetTop - headerHeight;
        window.scrollTo({
          top: scrollPosition,
          behavior: 'smooth'
        });
      }
    }, 100);
  };
  
  // Get display name for current tab
  const getTabDisplayName = (tab) => {
    const tabNames = {
      'home': 'Home',
      'big-movers': 'Big Movers',
      'xp-dashboard': 'Analytics',
      'level-up': 'Rank Up',
      explore: 'Explore',
      notifications: 'Notifications',
      profile: 'Profile',
      chat: 'Live Chat',
      leaderboard: 'Leaderboard & Price'
    };
    return tabNames[tab] || 'Home';
  };
  
  // Handle Make a Call button with smooth scroll
  const handleMakeCallClick = () => {
    if (activeTab !== 'home') {
      setActiveTab('home');
    }
    // Set to automated/calls tab after navigation
    setTimeout(() => {
      setPostType('automated');
      // Scroll to top where the trading interface will be
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }, 100);
  };
  
  // Helper function to handle user selection with scroll reset
  const handleUserSelection = (user) => {
    // If selecting current user, ensure we use the full currentUser data with banner
    if (user && currentUser && user.uid === currentUser.uid) {
      setSelectedUser(currentUser);
    } else {
      setSelectedUser(user);
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  // Check for pending rank up
  const checkPendingRankUp = useCallback(async () => {
    if (!currentUser) {
      setHasPendingRankUp(false);
      return;
    }
    
    try {
      const pending = await userService.hasPendingRankUp(currentUser.uid);
      setHasPendingRankUp(pending);
    } catch (error) {
      logger.error('Error checking pending rank up:', error);
    }
  }, [currentUser]);
  
  // Handle rank up click
  const handleRankUpClick = async () => {
    if (!currentUser || !hasPendingRankUp) return;
    
    try {
      const result = await userService.unlockRankUp(currentUser.uid);
      if (result.success) {
        setHasPendingRankUp(false);
        setToastMessage(result.message);
        
        // Show popup notification
        const rankUpNotification = {
          id: Date.now(),
          title: `🎉 Rank Up Unlocked!`,
          message: result.message,
          type: 'level_up',
          timestamp: new Date()
        };
        setPopupNotifications(prev => [...prev, rankUpNotification]);
      }
    } catch (error) {
      logger.error('Error unlocking rank up:', error);
      setToastMessage('Error unlocking rank up. Please try again.');
    }
  };
  
  
  const [priceData, setPriceData] = useState({
    price: 0,
    change24h: 0,
    marketCap: 0,
    volume24h: 0,
    liquidity: 0
  });
  const [priceHistory, setPriceHistory] = useState([]);
  const [priceTimestamps, setPriceTimestamps] = useState([]);
  const [hoveredPrice, setHoveredPrice] = useState(null);
  const [hoveredTime, setHoveredTime] = useState(null);
  const [mouseX, setMouseX] = useState(null);
  const [chartWidth, setChartWidth] = useState(0);
  const [chartMode, setChartMode] = useState('marketCap'); // 'price' or 'marketCap'
  const [chartTimePeriod, setChartTimePeriod] = useState('24h'); // '24h', '12h', '1h', '5m'

  // Token address - using environment variable or fallback for testing
  const tokenAddress = process.env.REACT_APP_TOPBLAST_TOKEN_ADDRESS || '5z3EqYQo9HiCEs3R84RCDMu2n7anpDMxRhdK8PSWmrRC'; // PONKE for testing

  // Fetch price data from DexScreener
  const fetchPriceData = useCallback(async () => {
    if (!tokenAddress) {
      // Use placeholder data when token isn't launched yet
      setPriceData({
        price: 0.00069,
        change24h: 420.69,
        marketCap: 69420000,
        volume24h: 420690,
        liquidity: 696969
      });
      // Generate fake price history with timestamps based on time period
      const now = Date.now();
      let dataPoints = 48;
      let interval = 5 * 60000; // 5 minutes default
      
      switch(chartTimePeriod) {
        case '5m':
          dataPoints = 60; // 60 one-minute candles
          interval = 60000; // 1 minute
          break;
        case '1h':
          dataPoints = 60; // 60 one-minute candles
          interval = 60000; // 1 minute
          break;
        case '12h':
          dataPoints = 48; // 48 fifteen-minute candles
          interval = 15 * 60000; // 15 minutes
          break;
        case '24h':
        default:
          dataPoints = 48; // 48 thirty-minute candles
          interval = 30 * 60000; // 30 minutes
          break;
      }
      
      const fakeHistory = Array.from({length: dataPoints}, (_, i) => {
        const basePrice = 0.00069;
        const baseMcap = 69420000;
        const progress = i / dataPoints;
        // Create more realistic price movement
        const trend = chartTimePeriod === '5m' ? 0.00002 : 
                     chartTimePeriod === '1h' ? 0.00005 : 
                     chartTimePeriod === '12h' ? -0.00008 : 
                     -0.00001;
        const noise = (Math.random() - 0.5) * 0.00003;
        const variation = (progress * trend) + noise;
        const mcapVariation = variation * 100000000;
        return {
          price: basePrice + variation,
          marketCap: baseMcap + mcapVariation
        };
      });
      const fakeTimestamps = Array.from({length: dataPoints}, (_, i) => {
        return now - (dataPoints - i) * interval;
      });
      setPriceHistory(fakeHistory);
      setPriceTimestamps(fakeTimestamps);
      return;
    }

    try {
      logger.log('Fetching price data for token:', tokenAddress);
      const response = await fetch(`https://api.dexscreener.com/latest/dex/tokens/${tokenAddress}`);
      
      if (!response.ok) {
        logger.error('DexScreener API error:', response.status, response.statusText);
        throw new Error(`API responded with ${response.status}`);
      }
      
      const data = await response.json();
      logger.log('Raw DexScreener response:', data);
      
      if (data && data.pairs && data.pairs.length > 0) {
        // Get the pair with highest liquidity
        const mainPair = data.pairs.reduce((prev, current) => 
          (current.liquidity?.usd || 0) > (prev.liquidity?.usd || 0) ? current : prev
        );
        
        const currentPrice = parseFloat(mainPair.priceUsd) || 0;
        
        // Log data to verify we're getting correct values
        logger.log('DexScreener Data for TopBlast:', {
          tokenAddress: tokenAddress,
          pair: mainPair.pairAddress,
          price: mainPair.priceUsd,
          volume24h: mainPair.volume?.h24,
          liquidity: mainPair.liquidity?.usd,
          marketCap: mainPair.marketCap,
          fdv: mainPair.fdv,
          change24h: mainPair.priceChange?.h24,
          baseToken: mainPair.baseToken?.symbol,
          allFields: mainPair // Log all fields to see what's available
        });
        
        setPriceData({
          price: currentPrice,
          change24h: parseFloat(mainPair.priceChange?.h24) || 0,
          marketCap: parseFloat(mainPair.marketCap) || parseFloat(mainPair.fdv) || 0,
          volume24h: parseFloat(mainPair.volume?.h24) || 0,
          liquidity: parseFloat(mainPair.liquidity?.usd) || 0
        });
        
        // Update price history and timestamps (keep last 48 points for 4 hours of data)
        const now = Date.now();
        const currentMarketCap = parseFloat(mainPair.marketCap) || parseFloat(mainPair.fdv) || 0;
        
        // Generate data based on time period
        let dataPoints = 48;
        let intervalMinutes = 5;
        
        switch(chartTimePeriod) {
          case '5m':
            dataPoints = 60; // 60 points at 5 seconds each = 5 minutes
            intervalMinutes = 5 / 60; // 5 seconds
            break;
          case '1h':
            dataPoints = 60; // 60 points at 1 minute each = 1 hour
            intervalMinutes = 1;
            break;
          case '12h':
            dataPoints = 48; // 48 points at 15 minutes each = 12 hours
            intervalMinutes = 15;
            break;
          case '24h':
          default:
            dataPoints = 48; // 48 points at 30 minutes each = 24 hours
            intervalMinutes = 30;
            break;
        }
        
        // Generate historical data with more realistic variations
        const fakeHistory = Array.from({length: dataPoints}, (_, i) => {
          const progress = i / dataPoints;
          // Different patterns for different time periods
          let variation = 0;
          
          // Create a trend that leads to the current price
          const trendFactor = (progress - 1); // Goes from -1 to 0
          
          if (chartTimePeriod === '5m') {
            variation = trendFactor * 0.002 + (Math.random() - 0.5) * 0.001; // Small variations for 5m
          } else if (chartTimePeriod === '1h') {
            variation = trendFactor * 0.01 + Math.sin(progress * Math.PI * 2) * 0.005 + (Math.random() - 0.5) * 0.002;
          } else if (chartTimePeriod === '12h') {
            variation = trendFactor * 0.03 + Math.sin(progress * Math.PI) * 0.01 + (Math.random() - 0.5) * 0.005;
          } else {
            variation = trendFactor * 0.05 + Math.sin(progress * Math.PI * 0.5) * 0.02 + (Math.random() - 0.5) * 0.01;
          }
          
          // As we approach the end, reduce variation to smoothly connect to current price
          if (i >= dataPoints - 5) {
            const smoothFactor = (dataPoints - i) / 5;
            variation = variation * smoothFactor;
          }
          
          return {
            price: currentPrice * (1 + variation),
            marketCap: currentMarketCap * (1 + variation)
          };
        });
        
        // Add current price as last point - ensure it's exactly the current price
        fakeHistory[fakeHistory.length - 1] = {
          price: currentPrice,
          marketCap: currentMarketCap
        };
        
        setPriceHistory(fakeHistory);
        
        // Generate matching timestamps
        const timestamps = Array.from({length: dataPoints}, (_, i) => {
          const minutesBack = (dataPoints - 1 - i) * intervalMinutes;
          return now - (minutesBack * 60000);
        });
        setPriceTimestamps(timestamps);
      } else {
        // No pairs found - token not trading yet, show placeholder data
        logger.log('No trading pairs found for token:', tokenAddress);
        setPriceData({
          price: 0.00069,
          change24h: 420.69,
          marketCap: 69420000,
          volume24h: 420690,
          liquidity: 696969
        });
        
        // Generate fake price history with timestamps based on time period
        const now = Date.now();
        let dataPoints = 48;
        let interval = 5 * 60000; // 5 minutes default
        
        switch(chartTimePeriod) {
          case '5m':
            dataPoints = 60; // 60 one-minute candles
            interval = 60000; // 1 minute
            break;
          case '1h':
            dataPoints = 60; // 60 one-minute candles
            interval = 60000; // 1 minute
            break;
          case '12h':
            dataPoints = 48; // 48 fifteen-minute candles
            interval = 15 * 60000; // 15 minutes
            break;
          case '24h':
          default:
            dataPoints = 48; // 48 thirty-minute candles
            interval = 30 * 60000; // 30 minutes
            break;
        }
        
        const fakeHistory = Array.from({length: dataPoints}, (_, i) => {
          const basePrice = 0.00069;
          const baseMcap = 69420000;
          const progress = i / dataPoints;
          // Create more realistic price movement
          const trend = chartTimePeriod === '5m' ? 0.00002 : 
                       chartTimePeriod === '1h' ? 0.00005 : 
                       chartTimePeriod === '12h' ? -0.00008 : 
                       -0.00001;
          const noise = (Math.random() - 0.5) * 0.00003;
          const variation = (progress * trend) + noise;
          const mcapVariation = variation * 100000000;
          return {
            price: basePrice + variation,
            marketCap: baseMcap + mcapVariation
          };
        });
        const fakeTimestamps = Array.from({length: dataPoints}, (_, i) => {
          return now - (dataPoints - i) * interval;
        });
        setPriceHistory(fakeHistory);
        setPriceTimestamps(fakeTimestamps);
      }
    } catch (error) {
      logger.error('Error fetching price data:', error);
      // Also show placeholder data on error
      setPriceData({
        price: 0.00069,
        change24h: 420.69,
        marketCap: 69420000,
        volume24h: 420690,
        liquidity: 696969
      });
    }
  }, [tokenAddress, chartTimePeriod]);

  // Regenerate price history when time period changes
  useEffect(() => {
    // Don't clear, just trigger a new fetch
    fetchPriceData();
  }, [chartTimePeriod, fetchPriceData]);

  // Fetch price data on mount and every 10 seconds
  useEffect(() => {
    fetchPriceData();
    const interval = setInterval(fetchPriceData, 10000); // Update every 10 seconds for more real-time data
    
    return () => clearInterval(interval);
  }, [fetchPriceData]);

  // Dark mode persistence - default to dark mode
  useEffect(() => {
    const savedDarkMode = localStorage.getItem('darkMode');
    // If no saved preference, default to dark mode (true)
    // Otherwise use the saved preference
    setDarkMode(savedDarkMode === null ? true : savedDarkMode === 'true');
  }, []);

  useEffect(() => {
    localStorage.setItem('darkMode', darkMode.toString());
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  // Handle scroll for floating button
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const postAreaHeight = 400; // Height of the post creation area
      
      // Show button when scrolled past the post area
      if (currentScrollY > postAreaHeight && currentScrollY > lastScrollY) {
        // Scrolling down
        setShowFloatingButton(true);
      } else if (currentScrollY < lastScrollY && currentScrollY < postAreaHeight) {
        // Scrolling up near top
        setShowFloatingButton(false);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [lastScrollY]);

  // Initialize XP betting service monitoring
  useEffect(() => {
    logger.log('🚀 Starting XP betting service monitoring...');
    xpService.startPriceMonitoring();
    
    return () => {
      xpService.stopPriceMonitoring();
    };
  }, []);

  // Email link sign-in and auth state are now handled by useAuth hook

  // Auth state change listener is now handled by useAuth hook

  // Load suggested user
  useEffect(() => {
    if (!isLoggedIn || !currentUser) return;

    const loadSuggestedUser = async () => {
      try {
        // Get all users
        const usersSnapshot = await getDocs(collection(db, 'users'));
        const users = [];
        const allUsers = [];
        
        for (const docSnapshot of usersSnapshot.docs) {
          if (docSnapshot.id !== currentUser.uid) {
            const userData = { uid: docSnapshot.id, ...docSnapshot.data() };
            
            // Get follower count for this user
            const followersQuery = query(
              collection(db, 'users'),
              where('following', 'array-contains', docSnapshot.id)
            );
            const followersSnapshot = await getDocs(followersQuery);
            
            const userWithFollowers = {
              ...userData,
              followerCount: followersSnapshot.size
            };
            
            // Skip blocked users
            if (!blockedUsers.includes(docSnapshot.id)) {
              allUsers.push(userWithFollowers);
              
              if (!following.includes(docSnapshot.id)) {
                users.push(userWithFollowers);
              }
            }
          }
        }
        
        // Pick a random user - prefer unfollowed users, but show any user if all are followed
        if (users.length > 0) {
          const randomUser = users[Math.floor(Math.random() * users.length)];
          setSuggestedUser(randomUser);
          setSuggestedUserStats({ followers: randomUser.followerCount });
        } else if (allUsers.length > 0) {
          // All users are being followed, show a random one anyway
          const randomUser = allUsers[Math.floor(Math.random() * allUsers.length)];
          setSuggestedUser(randomUser);
          setSuggestedUserStats({ followers: randomUser.followerCount });
        } else {
          // No users at all
          setSuggestedUser(null);
          setSuggestedUserStats({ followers: 0 });
        }
      } catch (error) {
        logger.error('Error loading suggested user:', error);
      }
    };

    loadSuggestedUser();
    
    // Refresh suggested user every 30 seconds
    const interval = setInterval(loadSuggestedUser, 30000);
    
    return () => clearInterval(interval);
  }, [isLoggedIn, currentUser, following, blockedUsers]);

  // Load total user count
  useEffect(() => {
    if (!isLoggedIn) return;

    const loadTotalUsers = async () => {
      try {
        const usersSnapshot = await getDocs(collection(db, 'users'));
        setTotalUsers(usersSnapshot.size);
      } catch (error) {
        logger.error('Error loading total users:', error);
      }
    };

    loadTotalUsers();
    // Refresh user count every 5 minutes
    const interval = setInterval(loadTotalUsers, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [isLoggedIn]);

  // Set page title and favicon
  useEffect(() => {
    document.title = 'TopBlast - Social Crypto Trading Platform';
    
    try {
      let link = document.querySelector("link[rel*='icon']");
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      link.type = 'image/gif';
      link.href = '/newlogo.gif';
    } catch (e) {
      logger.log('Favicon error:', e);
    }
  }, []);

  // Handle dropdown close on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      const dropdown = document.getElementById('profile-dropdown');
      const button = event.target.closest('[data-dropdown-toggle]');
      
      if (dropdown && !dropdown.contains(event.target) && !button) {
        dropdown.classList.add('hidden');
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Track scroll position for scroll-to-top button - Currently disabled
  // useEffect(() => {
  //   const handleScroll = () => {
  //     const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
  //     setShowScrollTop(scrollTop > 300); // Show after scrolling 300px
  //   };

  //   window.addEventListener('scroll', handleScroll);
  //   return () => window.removeEventListener('scroll', handleScroll);
  // }, []);

  // Handle wallet switch event
  useEffect(() => {
    const handleOpenWalletModal = () => {
      setShowWalletModal(true);
    };
    
    // Make it globally accessible
    window.handleSwitchWallet = handleOpenWalletModal;
    
    // Listen for custom event
    window.addEventListener('openWalletModal', handleOpenWalletModal);
    
    return () => {
      window.removeEventListener('openWalletModal', handleOpenWalletModal);
      delete window.handleSwitchWallet;
    };
  }, []);

  // Reset scroll position when changing tabs or viewing different profiles
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [activeTab, selectedUser]);

  // Ensure selectedUser is set when viewing profile
  useEffect(() => {
    if (activeTab === 'profile' && !selectedUser && currentUser) {
      setSelectedUser(currentUser);
    }
  }, [activeTab, selectedUser, currentUser]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.relative') || event.target.closest('.relative') !== event.currentTarget) {
        setActiveDropdown(null);
      }
    };

    if (activeDropdown) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [activeDropdown]);

  // Posts subscription is now handled by usePosts hook

  // Subscribe to live chat
  useEffect(() => {
    if (!isLoggedIn) return;

    const q = query(collection(db, 'chat'), orderBy('timestamp', 'desc'), limit(50));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const chatData = [];
      snapshot.forEach((docSnapshot) => {
        chatData.push({ id: docSnapshot.id, ...docSnapshot.data() });
      });
      // Reverse to show oldest first (proper chat order)
      setLiveChat(chatData.reverse());
    });

    return () => unsubscribe();
  }, [isLoggedIn]);

  // Auto-remove popup notifications after 5 seconds
  useEffect(() => {
    if (popupNotifications.length === 0) return;
    
    const timer = setTimeout(() => {
      setPopupNotifications(prev => prev.slice(1));
    }, 5000);
    
    return () => clearTimeout(timer);
  }, [popupNotifications]);

  // Initialize crowd-sourced checker when logged in
  useEffect(() => {
    if (isLoggedIn && currentUser) {
      // Start the crowd-sourced prediction checker
      crowdSourcedChecker.init();
      
      // Also check the current user's predictions
      crowdSourcedChecker.checkUserPredictions(currentUser.uid, 5);
    }
    
    // Cleanup when logging out
    return () => {
      crowdSourcedChecker.stop();
    };
  }, [isLoggedIn, currentUser]);

  // Subscribe to notifications
  useEffect(() => {
    if (!isLoggedIn || !currentUser) return;

    const q = query(
      collection(db, 'notifications'), 
      where('userId', '==', currentUser.uid),
      orderBy('timestamp', 'desc'),
      limit(20) // Reduced from 50
    );
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const notificationsData = [];
      let unreadCount = 0;
      const newNotifs = [];
      
      snapshot.forEach((docSnapshot) => {
        const notif = { id: docSnapshot.id, ...docSnapshot.data() };
        notificationsData.push(notif);
        if (!notif.read) {
          unreadCount++;
        }
      });
      
      // Check for new notifications to show as popups
      snapshot.docChanges().forEach((change) => {
        if (change.type === 'added') {
          const notif = { id: change.doc.id, ...change.doc.data() };
          // Only show popups for post notifications from people you follow
          if (notif.type === 'post' && !notif.read) {
            newNotifs.push(notif);
          }
        }
      });
      
      // Add new notifications to popup queue
      if (newNotifs.length > 0 && notifications.length > 0) { // Only after initial load
        setPopupNotifications(prev => [...prev, ...newNotifs]);
      }
      
      setNotifications(notificationsData);
      setUnreadNotifications(unreadCount);
    });

    return () => unsubscribe();
  }, [isLoggedIn, currentUser]);

  // Check for rank up notifications
  useEffect(() => {
    if (!currentUser) return;
    
    // Check for rank up notifications in the notifications array
    const hasRankUpNotification = notifications.some(
      notif => notif.type === 'level_up' && notif.actionRequired && !notif.read
    );
    
    // Also check user's pending rank up status
    if (hasRankUpNotification) {
      setHasPendingRankUp(true);
    } else {
      // Double-check with user service if no notification found
      checkPendingRankUp();
    }
  }, [notifications, currentUser, checkPendingRankUp]);

  // Subscribe to conversations
  useEffect(() => {
    if (!isLoggedIn || !currentUser) return;

    logger.log('Loading conversations for user:', currentUser.uid);
    const unsubscribe = loadConversations();
    
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [isLoggedIn, currentUser]);

  // Load suggested DM users (followers/following)
  useEffect(() => {
    if (!isLoggedIn || !currentUser) return;

    const loadSuggestedDMUsers = async () => {
      try {
        const suggestedUsers = [];
        
        // Get users we follow (excluding blocked users)
        if (following.length > 0) {
          const unblocked = following.filter(userId => !blockedUsers.includes(userId));
          for (const userId of unblocked.slice(0, 3)) { // Get up to 3 users we follow
            const userDoc = await getDoc(doc(db, 'users', userId));
            if (userDoc.exists()) {
              suggestedUsers.push({
                uid: userId,
                ...userDoc.data()
              });
            }
          }
        }
        
        // Get some followers if we need more suggestions
        if (suggestedUsers.length < 5) {
          const followersQuery = query(
            collection(db, 'users'),
            where('following', 'array-contains', currentUser.uid),
            limit(5 - suggestedUsers.length)
          );
          const followersSnapshot = await getDocs(followersQuery);
          followersSnapshot.forEach(doc => {
            if (!suggestedUsers.find(u => u.uid === doc.id) && !blockedUsers.includes(doc.id)) {
              suggestedUsers.push({
                uid: doc.id,
                ...doc.data()
              });
            }
          });
        }
        
        // If still need more, get random users
        if (suggestedUsers.length < 5) {
          const allUsersSnapshot = await getDocs(collection(db, 'users'));
          const randomUsers = [];
          allUsersSnapshot.forEach(doc => {
            if (doc.id !== currentUser.uid && !suggestedUsers.find(u => u.uid === doc.id) && !blockedUsers.includes(doc.id)) {
              randomUsers.push({
                uid: doc.id,
                ...doc.data()
              });
            }
          });
          
          // Shuffle and take what we need
          const shuffled = randomUsers.sort(() => 0.5 - Math.random());
          suggestedUsers.push(...shuffled.slice(0, 5 - suggestedUsers.length));
        }
        
        setSuggestedDMUsers(suggestedUsers.slice(0, 5));
      } catch (error) {
        logger.error('Error loading suggested DM users:', error);
      }
    };

    loadSuggestedDMUsers();
  }, [isLoggedIn, currentUser, following, blockedUsers]);

  // Subscribe to messages when conversation is selected
  useEffect(() => {
    if (!selectedConversation || selectedConversation === 'new') return;

    const conversationId = selectedConversation.id;
    if (!conversationId) return;

    // Clear the message input when switching conversations
    setNewDM('');
    
    const unsubscribe = loadMessages(conversationId);
    markMessagesAsRead(conversationId);
    
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [selectedConversation]);

  // Load user stats
  useEffect(() => {
    if (!selectedUser) return;

    const loadUserStats = async () => {
      try {
        const userId = selectedUser.uid || selectedUser.userId;
        
        // If viewing own profile, use the already loaded following state
        if (userId === currentUser?.uid) {
          const followersQuery = query(
            collection(db, 'users'),
            where('following', 'array-contains', userId)
          );
          const followersSnapshot = await getDocs(followersQuery);
          
          setUserStats({
            followers: followersSnapshot.size,
            following: following.length
          });
        } else {
          // For other users, fetch their data
          const userDoc = await getDoc(doc(db, 'users', userId));
          let followingCount = 0;
          
          if (userDoc.exists()) {
            const userData = userDoc.data();
            followingCount = userData.following?.length || 0;
            
            // Update selectedUser with the full data
            setSelectedUser(prev => ({
              ...prev,
              ...userData,
              uid: userId,
              banner: userData.banner || null
            }));
          }
          
          // Get followers count
          const followersQuery = query(
            collection(db, 'users'),
            where('following', 'array-contains', userId)
          );
          const followersSnapshot = await getDocs(followersQuery);
          
          setUserStats({
            followers: followersSnapshot.size,
            following: followingCount
          });
        }
      } catch (error) {
        logger.error('Error loading user stats:', error);
      }
    };

    loadUserStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUser?.uid, selectedUser?.userId, following.length, currentUser?.uid]);

  // Load comments for a post
  useEffect(() => {
    if (!viewingPost) return;

    const loadComments = async () => {
      try {
        // First try with orderBy
        const q = query(
          collection(db, 'comments'), 
          where('postId', '==', viewingPost.id),
          orderBy('timestamp', 'desc')
        );
        
        const unsubscribe = onSnapshot(q, async (snapshot) => {
          const commentsData = [];
          for (const docSnapshot of snapshot.docs) {
            const commentData = { id: docSnapshot.id, ...docSnapshot.data() };
            
            if (commentData.userId) {
              try {
                const userDoc = await getDoc(doc(db, 'users', commentData.userId));
                if (userDoc.exists()) {
                  commentData.gender = userDoc.data().gender;
                }
              } catch (error) {
                logger.error('Error getting user data:', error);
              }
            }
            
            commentsData.push(commentData);
          }
          setPostComments(commentsData);
        }, (error) => {
          // If orderBy fails due to missing index, try without it
          logger.log('Trying without orderBy due to:', error);
          const simpleQuery = query(
            collection(db, 'comments'), 
            where('postId', '==', viewingPost.id)
          );
          
          onSnapshot(simpleQuery, async (snapshot) => {
            const commentsData = [];
            for (const docSnapshot of snapshot.docs) {
              const commentData = { id: docSnapshot.id, ...docSnapshot.data() };
              
              if (commentData.userId) {
                try {
                  const userDoc = await getDoc(doc(db, 'users', commentData.userId));
                  if (userDoc.exists()) {
                    commentData.gender = userDoc.data().gender;
                  }
                } catch (error) {
                  logger.error('Error getting user data:', error);
                }
              }
              
              commentsData.push(commentData);
            }
            // Sort manually if orderBy wasn't available
            commentsData.sort((a, b) => {
              if (!a.timestamp || !b.timestamp) return 0;
              return b.timestamp.toMillis() - a.timestamp.toMillis();
            });
            setPostComments(commentsData);
          });
        });

        return () => unsubscribe();
      } catch (error) {
        logger.error('Error loading comments:', error);
      }
    };

    loadComments();
  }, [viewingPost]);

  // Initialize profile form
  useEffect(() => {
    if (selectedUser && selectedUser.uid === currentUser?.uid) {
      setProfileForm({
        username: selectedUser.username || '',
        bio: selectedUser.bio || ''
      });
    }
  }, [selectedUser, currentUser]);

  // Auto-switch to Posts tab when screen size changes
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      
      // If on chat tab and screen gets bigger (xl breakpoint = 1280px)
      if (activeTab === 'chat' && width >= 1280) {
        setActiveTab('home');
      }
      
      // If on leaderboard tab and screen gets bigger (lg breakpoint = 1024px)
      if (activeTab === 'leaderboard' && width >= 1024) {
        setActiveTab('home');
      }
    };

    window.addEventListener('resize', handleResize);
    
    // Check on mount
    handleResize();
    
    return () => window.removeEventListener('resize', handleResize);
  }, [activeTab]);

  // Auth handlers (handleSignup, handleLogin, etc.) are now in useAuth hook

  const handleUpdateProfile = async () => {
    if (!currentUser || !profileForm.username.trim()) return;

    try {
      await updateDoc(doc(db, 'users', currentUser.uid), {
        username: profileForm.username.trim(),
        bio: profileForm.bio.trim(),
        handle: `@${profileForm.username.trim()}`,
        displayPreference: profileForm.displayPreference || 'marketcap'
      });

      const updatedUser = {
        ...currentUser,
        username: profileForm.username.trim(),
        bio: profileForm.bio.trim(),
        handle: `@${profileForm.username.trim()}`,
        displayPreference: profileForm.displayPreference || 'marketcap',
        banner: currentUser.banner || null
      };

      setCurrentUser(updatedUser);

      // Update selectedUser if it's the current user
      if (selectedUser && selectedUser.uid === currentUser.uid) {
        setSelectedUser(updatedUser);
      }

      setEditingProfile(false);
    } catch (error) {
      logger.error('Error updating profile:', error);
      alert('Error updating profile: ' + error.message);
    }
  };


  const handleSendMessage = async (room = 'general') => {
    logger.log('handleSendMessage called with room:', room);
    logger.log('newMessage:', newMessage);
    logger.log('currentUser:', currentUser);
    logger.log('userWallet:', userWallet);
    
    if (!newMessage.trim() || !currentUser) {
      logger.log('Cannot send message:', { 
        hasMessage: !!newMessage.trim(), 
        hasUser: !!currentUser 
      });
      return;
    }

    // Check required fields
    if (!currentUser.username) {
      alert('Please update your profile with a username before sending messages.');
      return;
    }

    // Check if user can send to holders room
    if (room === 'holders' && (!userWallet || !userWallet.isHolder)) {
      alert('You need to connect a wallet with TopBlast tokens to send messages in the holders chat.');
      return;
    }

    try {
      logger.log('Sending message:', {
        message: newMessage,
        user: currentUser.username,
        userId: currentUser.uid,
        avatar: currentUser.avatar || 'default',
        room: room
      });
      
      const messageData = {
        userId: currentUser.uid,
        user: currentUser.username,
        avatar: currentUser.avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${currentUser.username}`,
        message: newMessage.trim(),
        room: room, // Add room info
        isHolder: userWallet?.isHolder || false, // Add holder status
        timestamp: serverTimestamp()
      };
      
      logger.log('Message data to send:', messageData);
      
      const docRef = await addDoc(collection(db, 'chat'), messageData);
      
      logger.log('Message sent successfully with ID:', docRef.id);
      setNewMessage('');
    } catch (error) {
      logger.error('Detailed error sending message:', error);
      logger.error('Error code:', error.code);
      logger.error('Error message:', error.message);
      alert('Failed to send message: ' + error.message);
    }
  };

  // Wallet Functions
  const handleWalletConnect = (walletData) => {
    logger.log('🎯 App.js - handleWalletConnect called');
    logger.log('  Previous wallet:', userWallet?.address?.slice(0, 8));
    logger.log('  New wallet:', walletData?.address?.slice(0, 8));
    logger.log('  Full new address:', walletData?.address);
    
    // Update wallet state
    setUserWallet(walletData);
    
    // Force re-render with new key
    setWalletKey(prev => {
      logger.log('🔑 Incrementing walletKey from', prev, 'to', prev + 1);
      return prev + 1;
    });
    
    setShowWalletModal(false);
    
    // Emit a custom event for wallet change
    const walletChangeEvent = new CustomEvent('walletChanged', {
      detail: { address: walletData?.address }
    });
    logger.log('📡 Dispatching walletChanged event');
    window.dispatchEvent(walletChangeEvent);
    
    // Force a complete re-render of the active tab if it's home/automated
    if (activeTab === 'home' && postType === 'automated') {
      logger.log('🔄 Force refreshing automated trading view');
      setPostType('regular');
      setTimeout(() => setPostType('automated'), 100);
    }
  };

  const handleWalletDisconnect = () => {
    logger.log('🌐 App.js - Wallet disconnected');
    walletService.disconnect(); // Actually disconnect the wallet
    setUserWallet(null);
    setShowWalletModal(false);
  };

  const openWalletModal = () => {
    logger.log('🟢 openWalletModal called - showing wallet modal');
    setShowWalletModal(true);
  };

  // DM Functions


  const toggleFollow = async (userId) => {
    if (!currentUser) return;

    try {
      const userRef = doc(db, 'users', currentUser.uid);

      if (following.includes(userId)) {
        await updateDoc(userRef, {
          following: arrayRemove(userId)
        });
        setFollowing(following.filter(id => id !== userId));
      } else {
        await updateDoc(userRef, {
          following: arrayUnion(userId)
        });
        setFollowing([...following, userId]);

        await addDoc(collection(db, 'notifications'), {
          userId: userId,
          type: 'follow',
          fromUser: currentUser.username,
          fromUserId: currentUser.uid,
          fromAvatar: currentUser.avatar,
          read: false,
          timestamp: serverTimestamp()
        });
      }
    } catch (error) {
      logger.error('Error toggling follow:', error);
    }
  };

  const handleShowFollowModal = async (type, userId) => {
    logger.log('handleShowFollowModal called:', { type, userId });
    setFollowModalType(type);
    setShowFollowModal(true);
    setLoadingFollowModal(true);
    setFollowModalUsers([]);

    try {
      if (type === 'followers') {
        // Get all users who follow this user
        logger.log('Loading followers for user:', userId);
        const q = query(
          collection(db, 'users'),
          where('following', 'array-contains', userId)
        );
        const snapshot = await getDocs(q);
        logger.log('Followers query returned:', snapshot.size, 'users');
        const users = [];
        snapshot.forEach(doc => {
          const userData = doc.data();
          logger.log('Follower:', doc.id, userData.username);
          users.push({ uid: doc.id, ...userData });
        });
        setFollowModalUsers(users);
      } else {
        // Get the users this user follows
        logger.log('Loading following for user:', userId);
        const userDoc = await getDoc(doc(db, 'users', userId));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const followingIds = userData.following || [];
          logger.log('User is following:', followingIds.length, 'users', followingIds);
          
          if (followingIds.length > 0) {
            // Fetch all followed users
            const users = [];
            for (const followedId of followingIds) {
              const followedDoc = await getDoc(doc(db, 'users', followedId));
              if (followedDoc.exists()) {
                const followedData = followedDoc.data();
                logger.log('Following user:', followedDoc.id, followedData.username);
                users.push({ uid: followedDoc.id, ...followedData });
              }
            }
            setFollowModalUsers(users);
          }
        }
      }
    } catch (error) {
      logger.error('Error loading follow data:', error);
      logger.error('Error details:', error.code, error.message);
    } finally {
      setLoadingFollowModal(false);
    }
  };

  const handleShowRetweetModal = async (postId) => {
    logger.log('handleShowRetweetModal called for post:', postId);
    setRetweetModalPostId(postId);
    setShowRetweetModal(true);
    setLoadingRetweetModal(true);
    setRetweetModalUsers([]);

    try {
      // Find all posts that are retweets of this post
      const q = query(
        collection(db, 'posts'),
        where('retweetOf', '==', postId)
      );
      const snapshot = await getDocs(q);
      logger.log('Retweet query returned:', snapshot.size, 'retweets');
      
      const users = [];
      const processedUserIds = new Set(); // Prevent duplicates

      for (const docSnapshot of snapshot.docs) {
        const retweetData = docSnapshot.data();
        if (!processedUserIds.has(retweetData.userId)) {
          processedUserIds.add(retweetData.userId);
          
          // Get follower count for sorting
          try {
            const followersQuery = query(
              collection(db, 'users'),
              where('following', 'array-contains', retweetData.userId)
            );
            const followersSnapshot = await getDocs(followersQuery);
            const followersCount = followersSnapshot.size;
            
            users.push({
              uid: retweetData.userId,
              username: retweetData.username,
              handle: retweetData.handle,
              avatar: retweetData.avatar,
              gender: retweetData.gender,
              bio: retweetData.bio || '',
              followersCount: followersCount
            });
          } catch (error) {
            logger.error('Error fetching follower count for retweet user:', retweetData.userId, error);
            users.push({
              uid: retweetData.userId,
              username: retweetData.username,
              handle: retweetData.handle,
              avatar: retweetData.avatar,
              gender: retweetData.gender,
              bio: retweetData.bio || '',
              followersCount: 0
            });
          }
        }
      }
      
      logger.log('Found retweet users:', users.length);
      setRetweetModalUsers(users);
      
      // Update the post's retweet count to match actual retweets
      const currentPost = posts.find(p => p.id === postId);
      if (currentPost && currentPost.retweets !== users.length) {
        logger.log('Updating retweet count from', currentPost.retweets, 'to', users.length);
        try {
          await updateDoc(doc(db, 'posts', postId), {
            retweets: users.length
          });
        } catch (error) {
          logger.error('Error updating retweet count:', error);
        }
      }
    } catch (error) {
      logger.error('Error loading retweet data:', error);
    } finally {
      setLoadingRetweetModal(false);
    }
  };

  const handleShowLikesModal = async (postId) => {
    logger.log('handleShowLikesModal called for post:', postId);
    setLikesModalPostId(postId);
    setShowLikesModal(true);
    setLoadingLikesModal(true);
    setLikesModalUsers([]);

    try {
      // Get the post to access its likes array
      const postDoc = await getDoc(doc(db, 'posts', postId));
      if (postDoc.exists()) {
        const postData = postDoc.data();
        const likes = postData.likes || [];
        logger.log('Post likes:', likes.length);
        
        if (likes.length > 0) {
          const users = [];
          
          // Fetch user data for each like
          for (const userId of likes) {
            try {
              const userDoc = await getDoc(doc(db, 'users', userId));
              if (userDoc.exists()) {
                const userData = userDoc.data();
                
                // Get follower count for sorting
                const followersQuery = query(
                  collection(db, 'users'),
                  where('following', 'array-contains', userId)
                );
                const followersSnapshot = await getDocs(followersQuery);
                const followersCount = followersSnapshot.size;
                
                users.push({
                  uid: userId,
                  username: userData.username,
                  handle: userData.handle,
                  avatar: userData.avatar,
                  gender: userData.gender,
                  bio: userData.bio || '',
                  followersCount: followersCount
                });
              }
            } catch (error) {
              logger.error('Error fetching user data for like:', userId, error);
            }
          }
          
          logger.log('Found like users:', users.length);
          setLikesModalUsers(users);
          
          // Update the post's likes array to match actual likes (in case of inconsistency)
          const currentPost = posts.find(p => p.id === postId);
          if (currentPost && currentPost.likes && currentPost.likes.length !== likes.length) {
            logger.log('Updating likes array from', currentPost.likes.length, 'to', likes.length);
            try {
              await updateDoc(doc(db, 'posts', postId), {
                likes: likes
              });
            } catch (error) {
              logger.error('Error updating likes array:', error);
            }
          }
        }
      }
    } catch (error) {
      logger.error('Error loading likes data:', error);
    } finally {
      setLoadingLikesModal(false);
    }
  };

  if (authLoading) {
    return (
      <div className={`min-h-screen ${darkMode ? 'bg-black' : 'bg-white'} flex items-center justify-center`}>
        <div className="text-center">
          <img 
            src="/newlogo.gif" 
            alt="TopBlast" 
            className="h-64 w-auto mx-auto mb-4"
          />
        </div>
      </div>
    );
  }

  if (!isLoggedIn) {
    return (
      <LoginPage 
        showSignup={showSignup}
        setShowSignup={setShowSignup}
        loginForm={loginForm}
        setLoginForm={setLoginForm}
        signupForm={signupForm}
        setSignupForm={setSignupForm}
        handleLogin={handleLogin}
        handleSignup={handleSignup}
        error={error}
        setError={setError}
        onBrowseWithoutAccount={handleBrowseWithoutAccount}
        // Enhanced auth props
        authMode={authMode}
        setAuthMode={setAuthMode}
        emailForPasswordless={emailForPasswordless}
        setEmailForPasswordless={setEmailForPasswordless}
        resetEmailSent={resetEmailSent}
        emailLinkSent={emailLinkSent}
        emailVerificationSent={emailVerificationSent}
        handlePasswordlessLogin={handlePasswordlessLogin}
        handlePasswordReset={handlePasswordReset}
        handleSignupWithVerification={handleSignupWithoutVerification}
        resendEmailVerification={resendEmailVerification}
        currentUser={currentUser}
        handleTabChange={handleTabChange}
        darkMode={darkMode}
      />
    );
  }

  if (loading) {
    const dataLoadingQuotes = [
      "Loading posts...",
      "Fetching the spiciest takes...",
      "Loading maximum toxicity...",
      "Preparing the timeline...",
      "Collecting degeneracy...",
      "Loading moon missions...",
      "Gathering diamond hand holders...",
      "Importing based opinions...",
      "Loading financial advice...",
      "Preparing for maximum gains...",
      "Loading cope and hopium...",
      "Synchronizing wallets..."
    ];
    
    const randomDataQuote = dataLoadingQuotes[Math.floor(Math.random() * dataLoadingQuotes.length)];
    
    return (
      <div className={`min-h-screen ${darkMode ? 'bg-black' : 'bg-white'} flex items-center justify-center`}>
        <div className="text-center">
          <img 
            src="/newlogo.gif" 
            alt="TopBlast" 
            className="h-64 w-auto mx-auto mb-4"
          />
          <p className="text-gray-500">{randomDataQuote}</p>
        </div>
      </div>
    );
  }

  // ===== Context Values =====
  const appContextValue = {
    darkMode,
    browsingMode,
    activeTab,
    setActiveTab,
    handleTabChange,
    handleUserSelection,
    setSelectedUser,
    setViewingImage,
  };

  const authContextValue = {
    currentUser,
    setCurrentUser,
    isLoggedIn,
    userWallet,
    openWalletModal,
    handleWalletDisconnect,
    following,
    setFollowing,
    blockedUsers,
    setBlockedUsers,
    toggleFollow,
  };

  const postsContextValue = {
    posts,
    feedFilter,
    setFeedFilter,
    postType,
    setPostType,
    newPost,
    setNewPost,
    showTradingPost,
    setShowTradingPost,
    postImagePreview,
    setPostImage,
    setPostImagePreview,
    handleImageSelect,
    postSentiment,
    setPostSentiment,
    uploadingImage,
    handlePost,
    handleTradingPost,
    likePost,
    retweetPost,
    handleDeletePost,
    handlePinPost,
    handleComment,
    likeComment,
    handleDeleteComment,
    hiddenPosts,
    setHiddenPosts,
    activeDropdown,
    setActiveDropdown,
    selectedPost,
    setSelectedPost,
    showCommentModal,
    setShowCommentModal,
    viewingPost,
    setViewingPost: handleViewPost,
    postComments,
    setPostComments,
    newComment,
    setNewComment,
    replyingTo,
    setReplyingTo,
    getPopularPosts,
    popularTimeFilter,
    setPopularTimeFilter,
  };

  return (
    <AppContext.Provider value={appContextValue}>
    <AuthContext.Provider value={authContextValue}>
    <PostsContext.Provider value={postsContextValue}>
    <div>
      <PerformanceMonitor />

      {/* Floating Active Boost Indicator */}
      {currentUser && <ActiveBoostIndicator compact />}
      
      {/* Global styles for scrollbars */}
      <style>{`
        .scrollbar-thin {
          scrollbar-width: thin;
          scrollbar-color: #1f2937 transparent;
        }
        .scrollbar-thin::-webkit-scrollbar {
          width: 6px;
        }
        .scrollbar-thin::-webkit-scrollbar-track {
          background: transparent;
        }
        .scrollbar-thin::-webkit-scrollbar-thumb {
          background-color: #1f2937;
          border-radius: 3px;
        }
        .scrollbar-thin::-webkit-scrollbar-thumb:hover {
          background-color: #374151;
        }
        .line-clamp-2 {
          overflow: hidden;
          display: -webkit-box;
          -webkit-box-orient: vertical;
          -webkit-line-clamp: 2;
        }
        @keyframes fade-in-down {
          0% {
            opacity: 0;
            transform: translate(-50%, -20px);
          }
          100% {
            opacity: 1;
            transform: translate(-50%, 0);
          }
        }
        .animate-fade-in-down {
          animation: fade-in-down 0.3s ease-out;
        }
      `}</style>
      
      {/* Image Modal */}
      {viewingImage && (
        <div 
          className="fixed inset-0 bg-black/90 flex items-center justify-center z-[60] p-4"
          onClick={() => setViewingImage(null)}
        >
          <div className="relative max-w-6xl max-h-[90vh]">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setViewingImage(null);
              }}
              className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors"
            >
              <X className="w-8 h-8" />
            </button>
            <img 
              src={viewingImage} 
              alt="Enlarged view" 
              className="max-w-full max-h-[90vh] object-contain cursor-pointer"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
      
      {/* Toast Notification */}
      {toastMessage && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-[70] animate-fade-in-down">
          <div className="bg-gradient-to-r from-pink-500 to-purple-600 text-white px-6 py-3 rounded-full shadow-lg flex items-center space-x-2">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span className="font-medium">{toastMessage}</span>
          </div>
        </div>
      )}
      
      <div className={`min-h-screen ${darkMode ? 'bg-black text-white' : 'bg-white text-black'} flex justify-center overflow-x-hidden`}>
        <div className="w-full max-w-[1400px] 3xl:max-w-[1800px] 4xl:max-w-[2400px] flex relative md:pt-0 pt-32 sm:pt-36 transition-all duration-500 ease-in-out">
          {/* Mobile Header */}
          <MobileHeader
            darkMode={darkMode}
            setDarkMode={setDarkMode}
            mobileMenuOpen={mobileMenuOpen}
            setMobileMenuOpen={setMobileMenuOpen}
            unreadNotifications={unreadNotifications}
            handleTabChange={handleTabChange}
            activeTab={activeTab}
            getTabDisplayName={getTabDisplayName}
          />

          {/* Mobile Post Button */}
          {activeTab !== 'messages' && activeTab !== 'chat' && (
            <button
              onClick={() => {
                if (activeTab !== 'home') {
                  handleTabChange('home');
                }
                setTimeout(() => {
                  const postBox = document.querySelector('textarea[placeholder*="Post whatever you want"]');
                  if (postBox) {
                    postBox.focus();
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }
                }, 100);
              }}
              className={`md:hidden fixed bottom-6 right-6 bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-full p-4 shadow-lg hover:shadow-xl transform hover:scale-110 z-40 transition-all duration-500 ease-in-out ${
                showFloatingButton 
                  ? 'translate-y-0 opacity-100 scale-100 animate-pulse-slow' 
                  : 'translate-y-20 opacity-0 scale-75 pointer-events-none'
              }`}
            >
              <Edit2 className="w-6 h-6" />
            </button>
          )}


          {/* Mobile Menu Overlay */}
          <MobileMenu
            mobileMenuOpen={mobileMenuOpen}
            setMobileMenuOpen={setMobileMenuOpen}
            darkMode={darkMode}
            setDarkMode={setDarkMode}
            activeTab={activeTab}
            handleTabChange={handleTabChange}
            unreadNotifications={unreadNotifications}
            unreadDMs={unreadDMs}
            currentUser={currentUser}
            handleUserSelection={handleUserSelection}
            handleLogout={handleLogout}
            hasPendingRankUp={hasPendingRankUp}
            onRankUpClick={handleRankUpClick}
          />

          {/* Desktop Sidebar */}
          <DesktopSidebar
            sidebarCollapsed={sidebarCollapsed}
            setSidebarCollapsed={setSidebarCollapsed}
            darkMode={darkMode}
            setDarkMode={setDarkMode}
            activeTab={activeTab}
            handleTabChange={handleTabChange}
            unreadNotifications={unreadNotifications}
            unreadDMs={unreadDMs}
            currentUser={currentUser}
            handleUserSelection={handleUserSelection}
            handleLogout={handleLogout}
            following={following}
            toggleFollow={toggleFollow}
            suggestedUser={suggestedUser}
            suggestedUserStats={suggestedUserStats}
            hasPendingRankUp={hasPendingRankUp}
            onRankUpClick={handleRankUpClick}
            setShowTradingPost={setShowTradingPost}
            setPostType={setPostType}
          />


          {/* Main Content */}
          <div className="relative md:border-x border-gray-800 flex-1 w-full md:max-w-2xl lg:max-w-4xl xl:max-w-4xl 2xl:max-w-7xl 3xl:max-w-[1200px] 4xl:max-w-[1600px] md:mx-auto transition-all duration-500 ease-in-out overflow-x-hidden" ref={mainContentRef}>
            
            {/* Dynamic Tab Navigation - positioned inside main content */}
            {activeTab !== 'profile' && (
              <div className={`hidden md:block sticky top-0 ${darkMode ? 'bg-black/80 border-gray-800' : 'bg-white/80 border-gray-200'} backdrop-blur-md border-b z-10`}>
                <div className="flex items-center justify-center px-4">
                  <div className="flex space-x-8">
                    {/* Main content tab */}
                    <button
                      onClick={() => {
                        const mainPage = activeTab === 'chat' || activeTab === 'leaderboard' 
                          ? (localStorage.getItem('lastMainPage') || 'home')
                          : activeTab;
                        handleTabChange(mainPage);
                      }}
                      className={`px-8 py-4 font-semibold transition-colors relative ${
                        activeTab !== 'chat' && activeTab !== 'leaderboard'
                          ? 'text-white' 
                          : 'text-gray-500 hover:text-gray-300'
                      }`}
                    >
                      {(() => {
                        const mainPage = activeTab === 'chat' || activeTab === 'leaderboard'
                          ? (localStorage.getItem('lastMainPage') || 'home')
                          : activeTab;
                        return getTabDisplayName(mainPage);
                      })()}
                      {activeTab !== 'chat' && activeTab !== 'leaderboard' && (
                        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-pink-500 to-purple-600 rounded-t-full" />
                      )}
                    </button>
                    
                    {/* Live Chat tab - show when sidebar would be hidden */}
                    <button
                      onClick={() => handleTabChange('chat')}
                      className={`px-6 py-4 font-semibold transition-colors relative xl:hidden ${
                        activeTab === 'chat'
                          ? 'text-white' 
                          : 'text-gray-500 hover:text-gray-300'
                      }`}
                    >
                      Live Chat
                      {activeTab === 'chat' && (
                        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-pink-500 to-purple-600 rounded-t-full" />
                      )}
                    </button>
                    
                    {/* Stats tab - show when sidebar would be hidden */}
                    <button
                      onClick={() => handleTabChange('leaderboard')}
                      className={`px-6 py-4 font-semibold transition-colors relative lg:hidden ${
                        activeTab === 'leaderboard'
                          ? 'text-white' 
                          : 'text-gray-500 hover:text-gray-300'
                      }`}
                    >
                      Leaderboard & Price
                      {activeTab === 'leaderboard' && (
                        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-pink-500 to-purple-600 rounded-t-full" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            
            {activeTab === 'home' ? (
                  <Suspense fallback={<LoadingSpinner text="Loading Home..." />}>
                    <HomeTab key={walletKey} />
                  </Suspense>
                ) : activeTab === 'big-movers' ? (
                  <Suspense fallback={<LoadingSpinner text="Loading Big Movers..." />}>
                    <BetterMoversTab
                    handleMakeCallClick={handleMakeCallClick}
                    />
                  </Suspense>
                ) : activeTab === 'following' ? (
                  <Suspense fallback={<LoadingSpinner text="Loading Following..." />}>
                    <FollowingTab />
                  </Suspense>
                ) : activeTab === 'post' ? (
                  <Suspense fallback={<LoadingSpinner text="Loading Post..." />}>
                    <PostDetailView
                    onRetweetCountClick={handleShowRetweetModal}
                    onLikesCountClick={handleShowLikesModal}
                    />
                  </Suspense>
                ) : activeTab === 'explore' ? (
                  <Suspense fallback={<LoadingSpinner text="Loading Explore..." />}>
                    <ExploreTab
                    suggestedUser={suggestedUser}
                    suggestedUserStats={suggestedUserStats}
                    />
                  </Suspense>
                ) : activeTab === 'notifications' ? (
                  <Suspense fallback={<LoadingSpinner text="Loading Notifications..." />}>
                    <NotificationsList
                    notifications={notifications}
                    posts={posts}
                    setViewingPost={handleViewPost}
                    setNotifications={setNotifications}
                    setUnreadNotifications={setUnreadNotifications}
                    formatTimestamp={formatTimestamp}
                    db={db}
                    toggleFollow={toggleFollow}
                    currentUser={currentUser}
                    darkMode={darkMode}
                    />
                  </Suspense>
                ) : activeTab === 'level-up' ? (
                  <Suspense fallback={<LoadingSpinner text="Loading Rank Up..." />}>
                    <RankUpTab />
                  </Suspense>
                ) : activeTab === 'xp-dashboard' ? (
                  <Suspense fallback={<LoadingSpinner text="Loading Analytics..." />}>
                    <Analytics />
                  </Suspense>
                ) : activeTab === 'chat' ? (
                  <div className="p-4 h-[calc(100vh-200px)] md:h-[600px]">
                    <Suspense fallback={<LoadingSpinner text="Loading Chat..." />}>
                      <LiveChat 
                      liveChat={liveChat}
                      newMessage={newMessage}
                      setNewMessage={setNewMessage}
                      handleSendMessage={handleSendMessage}
                      currentUser={currentUser}
                      handleUserSelection={handleUserSelection}
                      handleTabChange={handleTabChange}
                      blockedUsers={blockedUsers}
                      userWallet={userWallet}
                      onConnectWallet={openWalletModal}
                      />
                    </Suspense>
                  </div>
                ) : activeTab === 'leaderboard' ? (
                  <div className="p-4 space-y-4">
                    {/* Crypto Info Box - Full version with chart */}
                    <Suspense fallback={<LoadingSpinner text="Loading price data..." />}>
                      <CryptoWidget
                      priceData={priceData}
                      priceHistory={priceHistory}
                      priceTimestamps={priceTimestamps}
                      chartMode={chartMode}
                      chartTimePeriod={chartTimePeriod}
                      hoveredPrice={hoveredPrice}
                      hoveredTime={hoveredTime}
                      mouseX={mouseX}
                      chartWidth={chartWidth}
                      setChartMode={setChartMode}
                      setChartTimePeriod={setChartTimePeriod}
                      setMouseX={setMouseX}
                      setHoveredPrice={setHoveredPrice}
                      setHoveredTime={setHoveredTime}
                      setChartWidth={setChartWidth}
                      variant="full"
                      />
                    </Suspense>
                    
                    {/* Daily Leaderboard */}
                    <Suspense fallback={<LoadingSpinner text="Loading leaderboard..." />}>
                      <Leaderboard 
                      posts={posts} 
                      setViewingPost={handleViewPost}
                      setSelectedPost={setSelectedPost}
                      setSelectedUser={setSelectedUser}
                      setActiveTab={setActiveTab}
                      handleTabChange={handleTabChange}
                      handleUserSelection={handleUserSelection}
                      variant="full"
                      />
                    </Suspense>
                    
                    {/* Platform Stats */}
                    <Suspense fallback={<LoadingSpinner text="Loading stats..." />}>
                      <PlatformStats posts={posts} liveChat={liveChat} totalUsers={totalUsers} />
                    </Suspense>
                  </div>
                ) : activeTab === 'terms' ? (
                  <Suspense fallback={<LoadingSpinner text="Loading Terms..." />}>
                    <TermsTab darkMode={darkMode} />
                  </Suspense>
                ) : activeTab === 'tokenomics' ? (
                  <Suspense fallback={<LoadingSpinner text="Loading Tokenomics..." />}>
                    <TokenomicsTab 
                      darkMode={darkMode}
                      handleTabChange={handleTabChange}
                      setPostType={setPostType}
                    />
                  </Suspense>
                ) : activeTab === 'messages' ? (
                  <Suspense fallback={<LoadingSpinner text="Loading Messages..." />}>
                    <MessagesTab
                    conversations={conversations}
                    selectedConversation={selectedConversation}
                    setSelectedConversation={setSelectedConversation}
                    messages={messages}
                    newDM={newDM}
                    setNewDM={setNewDM}
                    dmRecipient={dmRecipient}
                    setDmRecipient={setDmRecipient}
                    userSearchResults={userSearchResults}
                    userSearchQuery={userSearchQuery}
                    setUserSearchQuery={setUserSearchQuery}
                    dmImage={dmImage}
                    dmImagePreview={dmImagePreview}
                    setDmImagePreview={setDmImagePreview}
                    sendingDM={sendingDM}
                    suggestedDMUsers={suggestedDMUsers}
                    blockedUsers={blockedUsers}
                    currentUser={currentUser}
                    handleDMImageSelect={handleDMImageSelect}
                    sendDM={sendDM}
                    searchUsers={searchUsers}
                    createNewConversation={createNewConversation}
                    handleUserSelection={handleUserSelection}
                    handleTabChange={handleTabChange}
                    formatTimestamp={formatTimestamp}
                    darkMode={darkMode}
                    />
                  </Suspense>
                ) : (
                  <Suspense fallback={<LoadingSpinner text="Loading Profile..." />}>
                    <ProfileView
                    user={selectedUser || currentUser}
                    userStats={userStats}
                    editingProfile={editingProfile}
                    setEditingProfile={setEditingProfile}
                    profileForm={profileForm}
                    setProfileForm={setProfileForm}
                    handleUpdateProfile={handleUpdateProfile}
                    handleShowFollowModal={handleShowFollowModal}
                    setDmRecipient={setDmRecipient}
                    setSelectedConversation={setSelectedConversation}
                    createNewConversation={createNewConversation}
                    initialTab={window.profileInitialTab || 'posts'}
                    />
                  </Suspense>
                )}
          </div>

          {/* Right Sidebar - Live Chat (hidden on mobile) */}
          {activeTab !== 'profile' && (
            <div className={`hidden xl:block h-screen sticky top-0 transition-all duration-500 ease-in-out ${
              minimizedSections.chat ? 'w-16' : 'w-72 xl:w-80 3xl:w-96 4xl:w-[480px]'
            }`}>
              {minimizedSections.chat ? (
                <div className="h-full flex flex-col items-center justify-start p-4">
                  <button
                    onClick={() => toggleMinimizeSection('chat')}
                    className="w-8 h-8 bg-gray-800 hover:bg-gray-700 rounded-lg flex items-center justify-center transition-colors"
                  >
                    <MessageSquare className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="h-full flex flex-col p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-bold">Live Chat</h3>
                    <button
                      onClick={() => toggleMinimizeSection('chat')}
                      className="w-6 h-6 bg-gray-800 hover:bg-gray-700 rounded flex items-center justify-center transition-colors"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="flex-1 min-h-0">
                    <Suspense fallback={<LoadingSpinner text="Loading chat..." />}>
                      <LiveChat 
                      liveChat={liveChat}
                      newMessage={newMessage}
                      setNewMessage={setNewMessage}
                      handleSendMessage={handleSendMessage}
                      currentUser={currentUser}
                      handleUserSelection={handleUserSelection}
                      handleTabChange={handleTabChange}
                      blockedUsers={blockedUsers}
                      userWallet={userWallet}
                      onConnectWallet={openWalletModal}
                      />
                    </Suspense>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Far Right - Trending (hidden on mobile) */}
          {activeTab !== 'profile' && (
            <div className={`hidden lg:block h-screen sticky top-0 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-800 scrollbar-track-transparent transition-all duration-500 ease-in-out ${
              minimizedSections.stats ? 'w-16' : 'w-72 xl:w-80 3xl:w-96 4xl:w-[480px]'
            }`}>
              {minimizedSections.stats ? (
                <div className="h-full flex flex-col items-center justify-start p-4">
                  <button
                    onClick={() => toggleMinimizeSection('stats')}
                    className="w-8 h-8 bg-gray-800 hover:bg-gray-700 rounded-lg flex items-center justify-center transition-colors"
                  >
                    <TrendingUp className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="p-4 space-y-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-bold">Leaderboard & TopBlast Price</h3>
                    <button
                      onClick={() => toggleMinimizeSection('stats')}
                      className="w-6 h-6 bg-gray-800 hover:bg-gray-700 rounded flex items-center justify-center transition-colors"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                  </div>
                  
                  {/* Crypto Info Box - Robinhood Style */}
                  <Suspense fallback={<LoadingSpinner text="Loading price..." />}>
                    <CryptoWidget
                    priceData={priceData}
                    priceHistory={priceHistory}
                    priceTimestamps={priceTimestamps}
                    chartMode={chartMode}
                    chartTimePeriod={chartTimePeriod}
                    hoveredPrice={hoveredPrice}
                    hoveredTime={hoveredTime}
                    mouseX={mouseX}
                    chartWidth={chartWidth}
                    setChartMode={setChartMode}
                    setChartTimePeriod={setChartTimePeriod}
                    setMouseX={setMouseX}
                    setHoveredPrice={setHoveredPrice}
                    setHoveredTime={setHoveredTime}
                    setChartWidth={setChartWidth}
                    />
                  </Suspense>
                  
                  <Suspense fallback={<LoadingSpinner text="Loading leaderboard..." />}>
                    <Leaderboard 
                    posts={posts} 
                    setViewingPost={handleViewPost}
                    setSelectedPost={setSelectedPost}
                    setSelectedUser={setSelectedUser}
                    setActiveTab={setActiveTab}
                    handleTabChange={handleTabChange}
                    handleUserSelection={handleUserSelection}
                    />
                  </Suspense>
                  
                  {/* Platform Stats */}
                  <Suspense fallback={<LoadingSpinner text="Loading stats..." />}>
                    <PlatformStats posts={posts} liveChat={liveChat} totalUsers={totalUsers} />
                  </Suspense>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <Suspense fallback={<LoadingSpinner text="Loading..." />}>
        <CommentModal />
      </Suspense>
      <Suspense fallback={<LoadingSpinner text="Loading..." />}>
        <FollowModal
        showFollowModal={showFollowModal}
        setShowFollowModal={setShowFollowModal}
        followModalType={followModalType}
        followModalUsers={followModalUsers}
        loadingFollowModal={loadingFollowModal}
        />
      </Suspense>
      
      <Suspense fallback={<LoadingSpinner text="Loading..." />}>
        <RetweetModal 
        isOpen={showRetweetModal}
        onClose={() => setShowRetweetModal(false)}
        retweetUsers={retweetModalUsers}
        loading={loadingRetweetModal}
        onUserClick={(user) => {
          handleUserSelection(user);
          handleTabChange('profile');
          setShowRetweetModal(false);
        }}
        />
      </Suspense>
      
      <Suspense fallback={<LoadingSpinner text="Loading..." />}>
        <LikesModal 
        isOpen={showLikesModal}
        onClose={() => setShowLikesModal(false)}
        likedUsers={likesModalUsers}
        loading={loadingLikesModal}
        onUserClick={(user) => {
          handleUserSelection(user);
          handleTabChange('profile');
          setShowLikesModal(false);
        }}
        />
      </Suspense>
      
      {/* Popup Notifications */}
      <div className="fixed top-20 right-4 z-50 space-y-2 pointer-events-none">
        {popupNotifications.slice(0, 3).map((notif, index) => (
          <Suspense key={notif.id} fallback={null}>
            <PopupNotification
            key={notif.id}
            notification={notif}
            index={index}
            onDismiss={(notifId) => setPopupNotifications(prev => prev.filter(n => n.id !== notifId))}
            onUserClick={(user) => {
              handleUserSelection(user);
              handleTabChange('profile');
            }}
            />
          </Suspense>
        ))}
      </div>

      {/* Wallet Connect Modal */}
      <WalletConnect
        isOpen={showWalletModal}
        onClose={() => setShowWalletModal(false)}
        onConnect={handleWalletConnect}
        onDisconnect={handleWalletDisconnect}
      />
    </div>
    </PostsContext.Provider>
    </AuthContext.Provider>
    </AppContext.Provider>
  );
};

// Wrapper component with Router
const TopBlast = () => {
  return (
    <Router>
      <Routes>
        <Route path="/*" element={<TopBlastApp />} />
      </Routes>
    </Router>
  );
};

export default TopBlast;